import React, { useEffect, useState, useContext } from 'react';
import {
  Modal,
  TextField,
  Button,
  ButtonGroup,
  Page,
  TextStyle
} from '@superops/ui-design-system';
import {
  KB_CONSTANTS,
  useKbContext,
  transformDataToTree,
  KB_ROUTE
} from '@nile/rmm/src/modules/knowledge-base';
import {
  CREATE_KB_COLLECTION,
  UPDATE_KB_COLLECTION
} from '@nile/rmm/src/modules/knowledge-base/graphql';
import { FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { useMutation } from '@apollo/client';
import { toast, getTranslatedText, EventEmitter } from '@nile/components';
import { AccountClient } from '@nile/graphql';
import { isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom';

function CreateCollection({
  toggleStatus,
  updateStatus,
  isSubCollection = false,
  isEditMode = false,
  initialData = {},
  resetHandleData,
  history
}) {
  const intl = useIntl();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const { treeItems, setTreeItems, activeViewConfig } = useKbContext();

  const { data: activeViewData, activeItemId } = activeViewConfig;

  useEffect(() => {
    if (isEditMode && !isEmpty(initialData?.data)) {
      setName(initialData?.data?.name);
    } else {
      setName('');
    }
  }, [isEditMode]);

  const [createCollection, { loading: createCollectionLoading }] = useMutation(
    CREATE_KB_COLLECTION,
    {
      client: AccountClient,
      onCompleted: (data) => {
        if (data?.createKbCollection) {
          const newTreeItem = [
            {
              ...data.createKbCollection
            }
          ];
          setTreeItems((prev) => transformDataToTree(newTreeItem, prev));
          toast('success', 'app.kb.collection.created.success.toast');
          // isSubCollection &&
          //   EventEmitter.dispatch(KB_CONSTANTS.KB_LIST_TABLE_REFETCH_EVENT);
          handleToggle();
          history.push(
            `${KB_ROUTE.COLLECTION_VIEW}/${data.createKbCollection.itemId}`
          );
        }
      },
      onError: () => {
        console.error('Error creating collection');
      }
    }
  );

  const [updateCollection, { loading: updateLoading }] = useMutation(
    UPDATE_KB_COLLECTION,
    {
      client: AccountClient,
      onCompleted: (data) => {
        if (data?.updateKbCollection) {
          const existingCollection = treeItems[data.updateKbCollection.itemId];
          const newTreeItem = [
            {
              ...data.updateKbCollection,
              children: existingCollection?.children
            }
          ];
          setTreeItems((prev) => transformDataToTree(newTreeItem, prev));
          toast('success', 'app.kb.collection.updated.success.toast');
          // EventEmitter.dispatch(
          //   KB_CONSTANTS.KB_LIST_TABLE_REFETCH_EVENT,
          //   activeViewConfig
          // );
          handleToggle();
        }
      },
      onError: (error) => {
        console.error('Error updating collection');
      }
    }
  );

  const handleToggle = () => {
    if (isEditMode) {
      resetHandleData();
    }
    updateStatus(!toggleStatus);
    // Reset state when closing
    if (toggleStatus) {
      setName('');
      setError('');
    }
  };

  const handleNameChange = (value) => {
    setName(value);
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleCreateOrUpdate = () => {
    if (!name.trim()) {
      setError(getTranslatedText(intl, 'app.kb.collection.error.nameRequired'));
      return;
    }

    if (isEditMode) {
      updateCollection({
        variables: {
          input: {
            name: name.trim(),
            itemId: initialData?.activeItemId
          }
        }
      });
      return;
    }

    const input = {
      name: name.trim(),
      parent: null
    };

    // Add parent only if it's a sub collection
    if (isSubCollection) {
      input.parent = {
        itemId: activeItemId
      };
    }

    createCollection({
      variables: {
        input
      }
    });
  };

  const getParentName = () => {
    if (isEditMode) {
      return initialData?.data?.parent?.name;
    }
    return activeViewData?.name || '';
  };

  const isLoading = createCollectionLoading || updateLoading;

  return (
    <>
      <Modal open={toggleStatus} medium showCloseButton={false}>
        <Page
          fullWidth
          variant="secondary"
          divider
          title={getTranslatedText(
            intl,
            isEditMode
              ? 'app.kb.rename.collection'
              : isSubCollection
              ? 'app.kb.create.new.sub.collection'
              : 'app.kb.create.new.collection'
          )}
          primaryAction={
            <ButtonGroup>
              <Button onClick={handleToggle} plain monochrome>
                {getTranslatedText(intl, 'common.cancel')}
              </Button>
              <Button
                primary
                onClick={handleCreateOrUpdate}
                icon={<SVGIcon name={isEditMode ? 'Edit' : 'Add'} />}
                loading={isLoading}
                disabled={isLoading}
              >
                {getTranslatedText(
                  intl,
                  isEditMode ? 'app.rename.btn' : 'common.create'
                )}
              </Button>
            </ButtonGroup>
          }
          contentHeight="100px"
          disableScroll
        >
          <div className="pt20 pb20 pl40 pr40">
            <TextField
              error={error}
              value={name}
              onChange={handleNameChange}
              placeholder={getTranslatedText(
                intl,
                isSubCollection
                  ? 'app.kb.label.name.sub.collection'
                  : 'app.kb.label.name.collection'
              )}
              autoComplete="off"
              fontSize="fontLarge"
              autoFocus
              maxLength={100}
              disabled={isLoading}
            />
            {isSubCollection && (
              <div className="mt20">
                <TextStyle>
                  <FormattedMessage id="app.kb.parent.collection.label" />
                </TextStyle>
                {' : '}
                <TextStyle weight="SemiBold">{getParentName()}</TextStyle>
              </div>
            )}
          </div>
        </Page>
      </Modal>
    </>
  );
}

export default withRouter(CreateCollection);
